/* Archived students page — 15-year retention for attestations. */
import { useEffect, useState } from 'react';
import { yearEndApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import AttestationMenu from '../components/students/AttestationMenu';
import { Archive, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_TONE = {
  archived: 'bg-slate-100 text-slate-700',
  graduated: 'bg-emerald-100 text-emerald-700',
};

const StatusChip = ({ status, reason }) => (
  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_TONE[status] || 'bg-slate-100 text-slate-700'}`}>
    {status === 'graduated' ? '🎓 Diplômé' : reason ? `📦 ${reason}` : '📦 Archivé'}
  </span>
);

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR'); }
  catch { return iso.slice(0, 10); }
};

const ArchivedStudents = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await yearEndApi.archived();
      setRows(data.students || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement archives');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((s) => {
    if (!q) return true;
    const hay = `${s.first_name} ${s.last_name} ${s.class_id}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const byStatus = filtered.reduce((acc, s) => {
    const k = s.status || 'archived';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="archived-students-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <Archive size={26} weight="duotone" className="text-[#64748B]" />
            Élèves archivés
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Historique conservé 15 ans · Attestations toujours disponibles.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]"
            />
            <Input
              className="pl-8 w-64"
              placeholder="Rechercher un élève…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="archived-search"
            />
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total archives" value={rows.length} testid="kpi-archived-total" />
        <KPI label="Transférés/Quittés" value={byStatus.archived || 0} testid="kpi-archived" tone="warn" />
        <KPI label="Diplômés" value={byStatus.graduated || 0} testid="kpi-graduated" tone="ok" />
        <KPI label="Affichés" value={filtered.length} testid="kpi-filtered" />
      </div>

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader>
          <CardTitle className="font-outfit text-base">Liste</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-[#64748B] py-8 text-center">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-[#64748B] py-8 text-center" data-testid="archived-empty">
              Aucun élève archivé pour le moment.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Prénom(s)</TableHead>
                  <TableHead>Dernière classe</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Attestations</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} data-testid={`archived-row-${s.id}`}>
                    <TableCell className="font-medium">{s.last_name}</TableCell>
                    <TableCell>{s.first_name}</TableCell>
                    <TableCell>{s.class_id || '—'}</TableCell>
                    <TableCell>
                      <StatusChip status={s.status} reason={s.archive_reason} />
                    </TableCell>
                    <TableCell className="text-xs text-[#64748B]">
                      {_fmtDate(s.archived_at || s.graduated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <AttestationMenu student={s} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KPI = ({ label, value, tone = 'default', testid }) => {
  const cls = {
    default: 'border-[#E2E8F0]',
    ok: 'border-emerald-200 bg-emerald-50/40 text-emerald-800',
    warn: 'border-amber-200 bg-amber-50/40 text-amber-800',
  }[tone];
  return (
    <div className={`border rounded p-3 ${cls}`} data-testid={testid}>
      <p className="text-[10px] uppercase opacity-70">{label}</p>
      <p className="font-outfit text-2xl font-bold">{value ?? 0}</p>
    </div>
  );
};

export default ArchivedStudents;
